import React, { createContext, useContext, useEffect, useState } from 'react';
import translations from '../locales/translations.json';

const I18nContext = createContext();

const LANGUAGES = ['fr', 'en', 'de'];
const CATEGORY_TO_KEY = {
  'All': 'all',
  'Dog': 'dog',
  'Cat': 'cat',
  'Wild Animals': 'wildAnimals',
};

const getBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return 'fr';
  const lang = navigator.language?.slice(0, 2)?.toLowerCase();
  return LANGUAGES.includes(lang) ? lang : 'fr';
};

const resolvePath = (obj, path) => path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

export const I18nProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('pawsEternalLanguage');
    return LANGUAGES.includes(saved) ? saved : getBrowserLanguage();
  });

  const setAppLanguage = (lang) => {
    if (!LANGUAGES.includes(lang)) return;
    setLanguage(lang);
    localStorage.setItem('pawsEternalLanguage', lang);
    document.documentElement.lang = lang;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (path, vars = {}) => {
    const scoped = resolvePath(translations[language], path);
    const fallback = resolvePath(translations.en, path);
    const value = scoped ?? fallback ?? path;
    if (typeof value !== 'string') return value;
    return value.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{{${k}}}`));
  };

  const translateCategory = (category) => {
    const key = CATEGORY_TO_KEY[category] || category;
    return t(`categories.${key}`);
  };

  const value = {
    language,
    setLanguage: setAppLanguage,
    t,
    translateCategory,
    languages: LANGUAGES,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used within I18nProvider');
  return context;
};
